import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchRoutes } from "@/lib/api";
import { RouteCard } from "@/components/RouteCard";

const CATEGORIES = ["All", "Coastal", "Mountain", "Peninsula"];
const DIFFICULTIES = ["All", "Beginner", "Intermediate", "Advanced"];

export default function RoutesList() {
    const [routes, setRoutes] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCategory = searchParams.get("category") || "All";
    const activeDifficulty = searchParams.get("difficulty") || "All";

    useEffect(() => {
        fetchRoutes().then(setRoutes).catch(() => {});
        document.title = "All Routes · MotorcycleRoutesIreland";
    }, []);

    const filtered = useMemo(() => {
        return routes.filter((r) => {
            if (activeCategory !== "All" && r.category !== activeCategory) return false;
            if (activeDifficulty !== "All" && r.difficulty !== activeDifficulty) return false;
            return true;
        });
    }, [routes, activeCategory, activeDifficulty]);

    const setFilter = (key, val) => {
        const next = new URLSearchParams(searchParams);
        if (val === "All") next.delete(key);
        else next.set(key, val);
        setSearchParams(next);
    };

    return (
        <div data-testid="routes-list-page">
            <section className="pt-40 pb-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow fade-up">The Collection</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter fade-up fade-up-delay-1">
                        Every road.<br />Curated.
                    </h1>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg max-w-2xl fade-up fade-up-delay-2">
                        A working library of Ireland's greatest motorcycle roads. Filter by character or difficulty and download the GPX from the route page.
                    </p>
                </div>
            </section>

            <section className="py-8 border-b border-[var(--border-hair)] sticky top-20 bg-[var(--surface)]/90 backdrop-blur-xl z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row gap-6 md:items-center">
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="eyebrow mr-2">Category</span>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={`filter-category-${c.toLowerCase()}`}
                                onClick={() => setFilter("category", c)}
                                className={`px-4 py-2 font-display font-bold uppercase tracking-[0.15em] text-xs border transition-all ${
                                    activeCategory === c
                                        ? "bg-[#E87722] text-black border-[#E87722]"
                                        : "text-[var(--fg-muted)] border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="eyebrow mr-2">Difficulty</span>
                        {DIFFICULTIES.map((d) => (
                            <button
                                key={d}
                                data-testid={`filter-difficulty-${d.toLowerCase()}`}
                                onClick={() => setFilter("difficulty", d)}
                                className={`px-4 py-2 font-display font-bold uppercase tracking-[0.15em] text-xs border transition-all ${
                                    activeDifficulty === d
                                        ? "bg-[#E87722] text-black border-[#E87722]"
                                        : "text-[var(--fg-muted)] border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722]"
                                }`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    {filtered.length === 0 ? (
                        <div data-testid="routes-empty-state" className="text-center py-24 font-body text-[var(--fg-dim)]">
                            No routes match those filters. Try a different combination.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map((r, i) => (
                                <RouteCard key={r.slug} route={r} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
